import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { MapPin, Envelope, Phone, Ticket, ArrowLeft, Info, WarningCircle } from "@phosphor-icons/react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { useFingerprint } from "@shared/hooks/useFingerprint";

export const Route = createFileRoute("/auth/register")({
  component: RegisterPage,
});

function RegisterPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [inviteCode, setInviteCode] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<"invite" | "details">("invite");
  const [error, setError] = useState<string | null>(null);
  const [cooldownDays, setCooldownDays] = useState<number | undefined>(undefined);

  // Folosim FingerprintJS pentru device fingerprinting
  const { fingerprint: deviceFingerprint } = useFingerprint();

  // Convex mutations
  const checkInvite = useQuery(
    api.auth.checkInviteCode,
    inviteCode.length >= 4 ? { code: inviteCode } : "skip"
  );
  const registerWithInvite = useMutation(api.auth.registerWithInvite);
  const sendOTP = useMutation(api.auth.sendOTP);

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Verifică codul de invitație
      if (checkInvite && !checkInvite.valid) {
        setError(checkInvite.error || "invalidInvite");
        setIsLoading(false);
        return;
      }

      // Cod valid - treci la pasul următor
      setStep("details");
    } catch (err) {
      console.error("Error checking invite:", err);
      setError("generic");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    
    // Așteaptă fingerprint-ul dacă nu e gata
    if (!deviceFingerprint) {
      setError("generic");
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      
      // Înregistrează utilizatorul
      const result = await registerWithInvite({
        inviteCode: inviteCode.toUpperCase(),
        email: email.toLowerCase(),
        phone: phone,
        deviceFingerprint: deviceFingerprint,
        userAgent: navigator.userAgent,
      });


      if (!result.success) {
        console.error("[Register] Registration failed:", result);
        const errorKey = result.error || "generic";
        console.error("[Register] Error key:", errorKey, "retryAfter:", result.retryAfter);
        
        // Handle rate limit errors with retryAfter
        if (result.retryAfter) {
          if (errorKey === "cooldown") {
            // For cooldown errors, use the cooldown message with retryAfter
            setError("cooldown");
            // Storefront retryAfter in seconds for display
            setCooldownDays(result.retryAfter); // Storefront seconds directly
          } else if (errorKey === "deviceDailyLimitReached" || errorKey === "ipDailyLimitReached") {
            // For daily limit errors, store retryAfter for display
            setError(errorKey);
            setCooldownDays(result.retryAfter);
          } else {
            // For other rate limit errors, use rateLimit messages
            setError(`rateLimit.registrationTooMany`);
          }
        } else {
          setError(errorKey);
          // Storefront cooldown days if provided
          if ("cooldownDays" in result && typeof result.cooldownDays === "number") {
            setCooldownDays(result.cooldownDays);
          } else {
            setCooldownDays(undefined);
          }
        }
        setIsLoading(false);
        return;
      }


      // Trimite OTP
      const otpResult = await sendOTP({
        phone: phone,
        type: "verify",
        deviceFingerprint: deviceFingerprint,
      });


      if (!otpResult.success) {
        console.error("[Register] OTP send failed:", otpResult);
        if (otpResult.retryAfter) {
          setError(`rateLimit.otpTooManyRequests`);
        } else {
          const errorKey = otpResult.error || "generic";
          console.error("[Register] OTP error key:", errorKey);
          setError(errorKey);
        }
        setIsLoading(false);
        return;
      }


      // Salvează datele în sessionStorage pentru pagina de verificare
      try {
        sessionStorage.setItem("pendingVerification", JSON.stringify({
          userId: result.userId,
          phone: phone,
          email: email,
          isRegister: true, // Flag pentru a distinge register de login
        }));
      } catch (err) {
        console.error("[Register] Failed to save pendingVerification:", err);
        setError("generic");
        setIsLoading(false);
        return;
      }


      // Redirecționează la verificare
      navigate({ to: "/auth/verify" });
    } catch (err) {
      console.error("[Register] Exception:", err);
      setError("generic");
    } finally {
      setIsLoading(false);
    }
  };

  // Verifică dacă codul de invitație este valid în timp real
  const isInviteValid = checkInvite?.valid === true;
  const inviteError = checkInvite?.valid === false ? checkInvite.error : null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4">
        {step === "details" ? (
          <button
            onClick={() => {
              setStep("invite");
              setError(null);
            }}
            className="inline-flex items-center gap-2 text-text-light hover:text-text transition-colors"
          >
            <ArrowLeft size={20} />
            {t("common:actions.back")}
          </button>
        ) : (
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-text-light hover:text-text transition-colors"
          >
            <ArrowLeft size={20} />
            {t("common:actions.back")}
          </Link>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex flex-col items-center mb-10">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4">
              <MapPin size={9} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-text">{t("register.title")}</h1>
            <p className="text-text-light mt-1">{t("register.subtitle")}</p>
          </div>

          {step === "invite" ? (
            /* Pasul 1: Cod invitație */
            <form onSubmit={handleInviteSubmit} className="space-y-6">
              {/* Info box */}
              <div className="flex items-start gap-3 p-4 bg-accent/20 rounded-xl">
                <Info size={20} className="text-accent-dark flex-shrink-0 mt-0.5" />
                <p className="text-sm text-text-light">
                  {t("register.inviteCodeRequired")}
                </p>
              </div>

              <div>
                <label
                  htmlFor="inviteCode"
                  className="block text-sm font-medium text-text mb-2"
                >
                  {t("register.inviteCode")}
                </label>
                <div className="relative">
                  <Ticket size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    id="inviteCode"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => {
                      setInviteCode(e.target.value.toUpperCase());
                      setError(null);
                    }}
                    placeholder={t("register.inviteCodePlaceholder")}
                    required
                    className={`w-full pl-12 pr-4 py-3 bg-white border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all uppercase ${
                      inviteError ? "border-error" : isInviteValid ? "border-success" : "border-border"
                    }`}
                  />
                </div>
                {/* Indicator validare în timp real */}
                {inviteCode.length >= 4 && (
                  <p className={`text-xs mt-2 ${isInviteValid ? "text-success" : inviteError ? "text-error" : "text-text-muted"}`}>
                    {isInviteValid
                      ? (checkInvite?.type === "influencer"
                          ? `✓ Cod influencer valid (+${checkInvite?.bonusInvites} invitații bonus)`
                          : "✓ Cod valid")
                      : inviteError
                        ? t(`errors.${inviteError}`)
                        : "Se verifică..."}
                  </p>
                )}
              </div>

              {/* Error message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-error/10 rounded-xl">
                  <WarningCircle size={20} className="text-error flex-shrink-0" />
                  <p className="text-sm text-error">
                    {error === "cooldown" && cooldownDays ? (
                      t("errors.cooldownWithTime", { seconds: cooldownDays })
                    ) : error === "deviceDailyLimitReached" && cooldownDays ? (
                      t("errors.deviceDailyLimitReachedWithTime", { seconds: cooldownDays })
                    ) : (
                      t(`errors.${error}`, { 
                        defaultValue: t("common:errors.generic"),
                        days: cooldownDays 
                      })
                    )}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || !inviteCode || !isInviteValid}
                className="w-full py-3.5 bg-primary-gradient text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-primary/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t("common:actions.loading") : t("common:actions.next")}
              </button>
            </form>
          ) : (
            /* Pasul 2: Detalii cont */
            <form onSubmit={handleRegisterSubmit} className="space-y-6">
              {/* Badge cod valid */}
              <div className="flex items-center justify-center gap-2 p-3 bg-success/10 rounded-xl">
                <Ticket size={20} className="text-success" />
                <span className="text-sm font-medium text-success">
                  {inviteCode}
                </span>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-text mb-2"
                >
                  {t("register.email")}
                </label>
                <div className="relative">
                  <Envelope size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    placeholder={t("register.emailPlaceholder")}
                    required
                    className="w-full pl-12 pr-4 py-3 bg-white border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-text mb-2"
                >
                  {t("register.phone")}
                </label>
                <div className="relative">
                  <Phone size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      setError(null);
                    }}
                    placeholder={t("register.phonePlaceholder")}
                    required
                    className="w-full pl-12 pr-4 py-3 bg-white border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="flex items-center gap-2 p-3 bg-error/10 rounded-xl">
                  <WarningCircle size={20} className="text-error flex-shrink-0" />
                  <p className="text-sm text-error">
                    {error === "cooldown" && cooldownDays ? (
                      t("errors.cooldownWithTime", { seconds: cooldownDays })
                    ) : error === "deviceDailyLimitReached" && cooldownDays ? (
                      t("errors.deviceDailyLimitReachedWithTime", { seconds: cooldownDays })
                    ) : (
                      t(`errors.${error}`, { 
                        defaultValue: t("common:errors.generic"),
                        days: cooldownDays 
                      })
                    )}
                  </p>
                </div>
              )}

              {/* Terms */}
              <p className="text-xs text-text-muted text-center">
                {t("register.terms")}{" "}
                <a href="#" className="text-primary hover:underline">
                  {t("register.termsLink")}
                </a>{" "}
                {t("register.privacy")}{" "}
                <a href="#" className="text-primary hover:underline">
                  {t("register.privacyLink")}
                </a>
              </p>

              <button
                type="submit"
                disabled={isLoading || !email || !phone}
                className="w-full py-3.5 bg-primary-gradient text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-primary/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t("common:actions.loading") : t("register.submit")}
              </button>
            </form>
          )}

          {/* Login link */}
          <div className="mt-8 text-center">
            <p className="text-text-light">
              {t("register.hasAccount")}{" "}
              <Link
                to="/auth/login"
                className="text-primary font-medium hover:underline"
              >
                {t("register.login")}
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
